<template>
  <el-main>
    <el-row type="flex" id="classes-container" :gutter="32">
      <el-col :md="4" :sm="8">
        <div class="filter-menu">
          <el-menu
            default-active="2"
            @select="onMenuChange"
            ref="menu"
            class="sidemenu filter-side-menu"
          >
            <el-menu-item index="1" filter="all">
              <all-icon />
              <span>{{ $t('classes.filter_menu.all') }}</span>
            </el-menu-item>
            <el-menu-item index="2" filter="active">
              <active-icon />
              <span>{{ $t('classes.filter_menu.active') }}</span>
            </el-menu-item>
            <el-menu-item index="3" filter="not_started">
              <not-started-icon />
              <span>{{ $t('classes.filter_menu.not_started') }}</span>
            </el-menu-item>
            <el-menu-item index="4" filter="draft">
              <draft-icon />
              <span>{{ $t('classes.filter_menu.draft') }}</span>
            </el-menu-item>
            <el-menu-item index="5" filter="finished">
              <finished-icon />
              <span>{{ $t('classes.filter_menu.finished') }}</span>
            </el-menu-item>
          </el-menu>
        </div>
      </el-col>
      <el-col :md="20" :sm="24">
        <div class="classes-table">
          <h1 class="blue-color"> {{ $t($i18nLabelsHelper(currentOrganizationType, 'groups')) }} </h1>
          <div v-if="classes && classes.length === 0 && queryString === null && status==='all'" class="no-classes">
            <p>{{ $t($i18nLabelsHelper(currentOrganizationType, 'no_groups_yet')) }}</p>
            <img src="@/assets/images/create-class-no-bg.svg" alt="" />
            <primary-button
              @click.native="onCreateClass"
              class="bigger"
              v-if="userCanCreateClass"
            >
              {{ $t($i18nLabelsHelper(currentOrganizationType, 'create_group')) }}
            </primary-button>
          </div>
          <div v-else>
            <el-form class="search-form">
              <elsa-search
                v-model="queryString"
                @search="searchClass"
              />
            </el-form>
            <div class="table-wrapper">
              <div v-if="userCanCreateClass" class="actions">
                <div class="options">
                  <el-collapse accordion>
                    <el-collapse-item
                      name="1"
                    >
                      <template slot="title">
                        <div class="button">
                          <i class="el-icon-circle-plus-outline"/>
                          <span> {{ $t($i18nLabelsHelper(currentOrganizationType, 'create_group')) }}</span>
                        </div>
                      </template>
                      <span
                        class="action-item create-class"
                        @click="onCreateClass"
                      >
                        <i class="elsa-icon-email" />
                        {{ $t($i18nLabelsHelper(currentOrganizationType, 'create_new_group')) }}
                      </span>
                      <span
                        v-if="isAdmin"
                        class="action-item import-users"
                        @click="onImportClass"
                      >
                        <i class="elsa-icon-import" />
                        {{ $t($i18nLabelsHelper(currentOrganizationType, 'import_groups')) }}
                      </span>
                    </el-collapse-item>
                  </el-collapse>
                  <div class="remove-action">
                    <chip-icon
                      v-if="showClassOptions"
                      @click.native="modals.showDeleteClassesModal = true;"
                      :tooltip="$t($i18nLabelsHelper(currentOrganizationType, 'modals.delete.title'))"
                      icon="elsa-icon-delete"
                    />
                  </div>
                </div>
              </div>

              <el-table
                :data="classes"
                style="width: 100%"
                :empty-text="$t('common.no_values')"
                @select="handleSelectRow"
                @select-all="handleSelectRow"
                @row-click="showDetails"
              >
                <el-table-column type="selection" />
                <base-table-column
                  :sort-by="sortByName"
                  sortable
                  prop="name"
                  :label="$t($i18nLabelsHelper(currentOrganizationType, 'group_title'))"
                />
                <base-table-column
                  :sort-by="sortByProp('start_date')"
                  sortable
                  dataType="date"
                  prop="start_date"
                  :label="$t('classes.table_titles.start_date')"
                />
                <base-table-column
                  :sort-by="sortByProp('end_date')"
                  sortable
                  dataType="date"
                  prop="end_date"
                  :label="$t('classes.table_titles.end_date')"
                />
                <status-table-column
                  sortable
                  prop="status"
                  :sort-by="sortByProgress"
                  :label="$t('classes.table_titles.status')"
                />
                <user-info-table-column
                  sortable
                  prop="owner"
                  :sort-by="sortByOwner"
                  :label="$t($i18nLabelsHelper(currentOrganizationType, 'teacher'))"
                  v-if="isAdmin || !isTeacher"
                />

                <students-table-column
                  sortable
                  :label="$t($i18nLabelsHelper(currentOrganizationType, 'students'))"
                  align="center"
                  prop="users_count"
                />
                <students-pending-invite-table-column
                  sortable
                  :label="$t('common.pending_invites')"
                  align="center"
                  prop="pending_invitations"
                />
              </el-table>
              <div class="pagination">
                <pagination
                  :page-size="maxNumberOfElements"
                  :total="pagination.total"
                  :pager-counter="5"
                  :current-page.sync="currentPage"
                  v-on:current-change="updateCurrentPage"
                />
                <over-table-pagination
                  class="over-table-pagination-right"
                  :total="pagination.total"
                  :currentPage="currentPage"
                  :pageSize="maxNumberOfElements"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="modals">
          <delete-classes
            :dialogVisible="modals.showDeleteClassesModal"
            v-on:closed="modals.showDeleteClassesModal = false"
            :numberOfSelectedClasses="numberOfSelectedRows"
            :currentOrganizationType="currentOrganizationType"
            @doDeleteClasses="doDeleteClasses"
          />
        </div>
      </el-col>
    </el-row>
  </el-main>
</template>

<script>
  import ActiveIcon from '@/assets/images/inline.active-icon.svg'
  import NotStartedIcon from '@/assets/images/inline.not-started-icon.svg'
  import DraftIcon from '@/assets/images/inline.draft-icon.svg'
  import AllIcon from '@/assets/images/inline.all-classes-icon.svg'
  import FinishedIcon from '@/assets/images/inline.finished-icon.svg'
  import PrimaryButton from '@/components/common/buttons/PrimaryButton'
  import OverTablePagination from '@/components/common/OverTablePagination'
  import BaseTableColumn from '@/components/classes/BaseTableColumn'
  import StudentsTableColumn from '@/components/classes/StudentsTableColumn'
  import StudentsPendingInviteTableColumn from '@/components/classes/StudentsPendingInviteTableColumn'
  import StatusTableColumn from '@/components/classes/StatusTableColumn'
  import UserInfoTableColumn from '@/components/classes/UserInfoTableColumn'
  import Pagination from '@/components/common/Pagination'
  import ElsaSearch from '@/components/common/Search'
  import { NUMBER_ELEMENTS_PER_PAGE } from "@/constants/generic"
  import { mapGetters, mapActions } from 'vuex'
  import { isEmpty, converge, prop, toLower, compose, propOr, pathOr, length, not, pluck } from 'ramda'
  import { ADMIN } from '../constants/userRoles'
  import { calculatePercentageOfDateInterval } from '../utils/dateInterval'
  import ChipIcon from '@/components/common/ChipIcon';
  import DeleteClasses from '@/components/modals/classes/DeleteClasses'
  import {
    warningMessage,
    successMessage
  } from '../utils/messages';

  export default {
    components: {
      ActiveIcon,
      NotStartedIcon,
      DraftIcon,
      AllIcon,
      FinishedIcon,
      PrimaryButton,
      OverTablePagination,
      BaseTableColumn,
      StatusTableColumn,
      UserInfoTableColumn,
      Pagination,
      StudentsTableColumn,
      ElsaSearch,
      StudentsPendingInviteTableColumn,
      ChipIcon,
      DeleteClasses
    },
    computed: {
      showClassOptions: compose(not, isEmpty, propOr([], 'selectedClasses')),
      numberOfSelectedRows: compose(length, propOr([], 'selectedClasses')),
      ...mapGetters('user', [ 'isElsaAdmin' ]),
      ...mapGetters({
        classes: 'classes/getClassList',
        pagination: 'classes/buildPagination',
        isTeacher: 'organization/isOrganizationTeacher',
        isAdmin: 'organization/isOrganizationAdmin',
        isAssociateAdmin: 'organization/isOrganizationAssociateAdmin',
        currentOrganizationId: 'organization/currentOrganizationId',
        currentOrganizationType: 'organization/currentOrganizationType',
        userHasOrganizationRole: 'organization/userHasOrganizationRole'
      }),
      maxNumberOfElements() {
        return NUMBER_ELEMENTS_PER_PAGE
      },
      userCanCreateClass() {
        const organizationId = this.currentOrganizationId
        return this.userHasOrganizationRole(ADMIN, organizationId)
        || this.isElsaAdmin
      },
    },
    props: {
      owner: {
        type: String,
        default: ''
      },
      organizationId: {},
      classId: {}
    },
    methods: {
      sortByProp: prop,
      sortByName: compose(toLower, propOr('', 'name')),
      sortByOwner: compose(toLower, pathOr('', ['owner', 'email'])),
      sortByProgress: converge(calculatePercentageOfDateInterval, [ prop('start_date'), prop('end_date'), prop('status') ]),
      showDetails(row) {
        const organizationId = this.currentOrganizationId
        const classId = row.id

        return this.$router.push({
          name: 'class-details',
          params: {
            organizationId,
            classId,
          }
        })
      },
      onCreateClass() {
        const organizationId = this.currentOrganizationId

        return this.$router.push({
           name: 'classes-new',
           params: {
             organizationId,
           }
        })
      },
      onImportClass() {
        const organizationId = this.currentOrganizationId

        return this.$router.push({
           name: 'classes-import',
           params: {
             organizationId,
           }
        })
      },
      updateCurrentPage(page) {
        this.currentPage = page;
        this.getClasses({ page, ...this.filterParams })
      },
      onMenuChange(index) {
        const filter = this.$refs.menu.items[index].$attrs.filter
        this.status = filter

        if (filter !== 'all') {
          this.filterParams = { ...this.filterParams, status: filter }
        } else {
          this.filterParams = { ...this.filterParams, status: '' }
        }
        this.getClasses(this.filterParams)
      },
      searchClass(name) {
        this.filterParams = { ...this.filterParams, search: name }
        this.getClasses({ search: name })
      },
      handleSelectRow(rowDat){
        this.selectedClasses = rowDat
      },
      doDeleteClasses() {
        const groupIds = compose(pluck('id'), propOr([], 'selectedClasses'))(this)
        const organizationId = this.currentOrganizationId;
        const deleteRequest = [];
        groupIds.forEach(groupId => {
          deleteRequest.push(this.deleteClass({organizationId, groupId}))
        });
        return Promise.all(deleteRequest).then((resp) => {
          const message = this.$i18nLabelsHelper(this.currentOrganizationType, 'delete_group_success')
          successMessage(this.$t(message))(this);
          this.getClasses({ ...this.filterParams, status: this.status })
        })
        .catch((error) => {
          const message = this.$i18nLabelsHelper(this.currentOrganizationType, 'delete_group_warning')
          return warningMessage(this.$t(message))(this)
        })
      },
      ...mapActions({
        getClasses: 'classes/getClasses',
        setCurrentClass: 'classes/setCurrentClass',
        getClassGoals: 'classes/getClassGoals',
        deleteClass: 'classes/deleteClass'
      })
    },
    data() {
      return {
        queryString: null,
        currentPage: 1,
        filterParams: {},
        status: 'all',
        showPendingUsersModal: '',
        selectedClasses: [],
        modals: {
          showDeleteClassesModal: false
        }
      }
    },
    async mounted () {
      const filterParams = isEmpty(this.owner) ? {} : { owner: this.owner }
      try {
        const status = this.status = 'active'
        await this.getClasses({ ...filterParams, status })
        await this.getPendingInviteUsers()
      } catch(error) {
        error
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "../scss/_colors.scss";

  #classes-container {
    margin: 15px 32px 40px 32px;
  }
  .classes-table {
    h1 {
      border: none;
    }
    @media only screen and (max-width: 992px) {
      margin-top: 30px;
    }
  }

  .filter-menu {
    margin-top: 40px;
  }

  .el-form.search-form {
    padding-bottom: 0;
    margin-top: 48px;
  }

  .no-classes {
    margin-top: 72px;
    display: flex;
    flex-direction: column;
    align-items: baseline;
    p {
      margin: 0;
      font-weight: 500;
      font-size: 18px;
      color: $dark-blue-2;
    }
    img {
      margin-top: 50px;
      margin-bottom: 63px;
    }
  }
  ::v-deep .search-box.elsa-input {
    margin-top: 16px;
    .el-input .el-input__inner {
      padding-left: 15px;
    }
  }

  .table-wrapper {
    border-top: 1px solid rgba(206, 224, 234, 0.5);
    margin-top: 25px;
  }

  .elsa-chip {
    text-transform: none;
    background-color: white;
    margin-top: 24px;
  }

  .el-table::v-deep .cell {
    padding-left: 10px;
  }


   .actions {
    margin-top: 24px;
    margin-bottom: 40px;
    display: flex;
    justify-content: flex-start;

    &::v-deep .el-collapse {
      border: none;

      .el-collapse-item__header {
        min-width: 185px;
        // width: 160px;
        height: 40px;
        background: $purple;
        border: 2px solid $purple;
        border-radius: 45px;
        font-weight: 600;
        font-size: 14px;
        color: white;
        text-align: center;
        .button {
          width: 100%;
          i {
            font-size: 18px;
            vertical-align: text-top;
            margin-right: 2px;
          }
        }
     }

      .el-collapse-item__wrap {
        position: absolute;
        z-index: 2;
        margin-top: 8px;
        box-shadow: 0px 4px 7px rgba(4, 33, 50, 0.1);
        padding: 12px 16px;
        min-width: 220px;
      }
      .el-collapse-item__content {
        padding-bottom: 0;
      }
    }
    .remove-action {
      padding-left: 15px;
    }
  }

    .options {
    display: flex;
    flex-direction: row;
    align-items: center;
    display: flex;
    justify-items: flex-start;
    height: 40px;
  }

  .actions .options ::v-deep .el-collapse .el-collapse-item__header {
    background: linear-gradient(180deg, #29F4FF -46.11%, #5257FF 151.97%);
    box-shadow: 0px 8px 18px rgba(69, 137, 255, 0.26);
    border-radius: 50px;
    font-weight: bold;
    font-size: 14px;
    color: white;
    padding-right: 10px;

    &.is-active {
      color: white;
      background-color: $purple;
    }
  }

  ::v-deep .action-item {
    display: flex;
    flex-direction: row;
    margin-top: auto;
    margin-bottom: auto;
    color: $purple;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    line-height: 22px;

    &:not(:last-of-type) {
      margin-bottom: 16px
    }

    &:hover {
      font-size: 14px;
      font-weight: 800;
    }

    i {
      border-width: 2px;
      height: 16px;
      width: 16px;
      padding-right: 8px;
      font-size: 16px;
      margin: auto 0;
    }
  }
</style>
